import React from "react";
import {
  Box,
  IconButton,
  Button,
  FormHelperText,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { FieldArray, useFormikContext } from "formik";
import HTMLEditor from "../../HtmlEditor/HtmlEditor";
import { Delete as DeleteIcon, Add } from "@mui/icons-material";
import { Select } from "../../FormikFields";
import OrderingList from "../../Lists/OrderingList";
import QuestionTextField from "./QuestionTextField";
import AnswersField from "./AnswersField";
import { v4 as uuid } from "uuid";
import { findByKey } from "../../../utils/helpers";
import { PropTypes } from "prop-types";

const orderingTextFieldToolbar =
  "undo redo | bold italic underline | alignment numlist bullist outdent indent | link image media fileUpload";

const getNewOrderingAnswer = order => ({
  order,
  text: "",
  id: uuid(),
  type: "answer",
  correct: true
});

const useStyles = makeStyles(() => ({
  answers: {
    overflowX: "auto"
  }
}));

const OrderingTextField = ({
  orderingItem,
  itemIndex,
  onDelete,
  onEdit,
  questionFilesList,
  setQuestionFilesList
}) => {
  const { t } = useTranslation();

  const placeholder = t("editQuestion.answerOptionPlaceholder", {
    index: itemIndex + 1
  });
  return orderingItem ? (
    <Box
      display="flex"
      alignItems="center"
      width="60%"
      justifyContent="space-between"
      data-qa="ordering-text-field"
    >
      <Box flex={1} minHeight="100%" maxWidth="100%">
        <HTMLEditor
          disableMargin
          onChange={onEdit}
          value={orderingItem.text}
          menubar={false}
          onFileAdd={(fileObj, tempLink, type) => {
            const list = questionFilesList;
            list.push({ fileObj, tempLink, type });
            setQuestionFilesList(list);
          }}
          plugins={["lists", "link", "image", "media", "code"]}
          toolbar={orderingTextFieldToolbar}
          inline={true}
          toolbarId={`toolbar-${orderingItem.id}`}
          disableResize
          placeholder={placeholder}
        />
      </Box>
      <Box display="flex" alignItems="center" ml={1}>
        <IconButton
          onClick={() => onDelete(orderingItem)}
          data-qa="ordering-text-delete-btn"
          size="large"
        >
          <DeleteIcon color="primary" />
        </IconButton>
      </Box>
    </Box>
  ) : null;
};

const OrderField = ({ orderIndex, numOrderingItems, onChange }) => {
  const menuItems = [...Array(numOrderingItems)].map((item, index) => ({
    label: index + 1,
    value: index
  }));
  return (
    <Box width={80}>
      <Select
        name={`answers[${orderIndex}].order`}
        menuItems={menuItems}
        renderValue={value => value + 1}
        onChange={onChange}
      />
    </Box>
  );
};

const OrderingFields = ({
  isSurveyQuestion,
  questionFilesList,
  setQuestionFilesList
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { errors, values, setFieldValue, submitCount } = useFormikContext();

  /**
   * When an item is deleted, we decrease the order of
   * all items whose order is greater than the deleted item by 1
   */
  const handleDeleteItem = deletedAnswerIndex => deletedAnswer => {
    values.answers.splice(deletedAnswerIndex, 1);
    values.answers = values.answers.map(answer => ({
      ...answer,
      order:
        answer.order > deletedAnswer.order ? answer.order - 1 : answer.order
    }));
    setFieldValue("answers", values.answers);
  };

  /**
   * When the admin selects a new order for an item,
   * we find the existing item with the new order and swap
   * their orders to ensure no two items have the same order
   */
  const handleOrderChange = reorderedAnswer => newOrder => {
    const newAnswers = [...values.answers];
    findByKey(newAnswers, "order", newOrder).order = reorderedAnswer.order;
    findByKey(newAnswers, "id", reorderedAnswer.id).order = newOrder;
    setFieldValue("answers", newAnswers);
  };

  return (
    <Box display="flex" flexDirection="column">
      <QuestionTextField
        questionFilesList={questionFilesList}
        setQuestionFilesList={setQuestionFilesList}
      />
      <AnswersField
        renderAnswers={() => (
          <Box
            display="flex"
            flexDirection="column"
            flex={1}
            className={classes.answers}
          >
            <Typography variant="subtitle1" sx={{ mt: 1, mb: 4.5 }}>
              {isSurveyQuestion
                ? t("editQuestion.orderingQuestionSurveyNote")
                : t("editQuestion.orderingQuestionQuizNote")}
            </Typography>
            <FieldArray
              name="answers"
              render={({ replace, push }) => (
                <Box display="flex" flexDirection="column" flex={1}>
                  <OrderingList
                    listItems={values.answers}
                    renderText={(listItem, index) => (
                      <OrderingTextField
                        onDelete={handleDeleteItem(index)}
                        onEdit={newText =>
                          replace(index, { ...listItem, text: newText })
                        }
                        itemIndex={index}
                        orderingItem={listItem}
                        questionFilesList={questionFilesList}
                        setQuestionFilesList={setQuestionFilesList}
                      />
                    )}
                    renderOrderField={
                      !isSurveyQuestion
                        ? (listItem, index) => (
                            <OrderField
                              numOrderingItems={values.answers.length}
                              orderIndex={index}
                              onChange={handleOrderChange(listItem)}
                            />
                          )
                        : null
                    }
                  />
                  {!!errors.answers && submitCount > 0 && (
                    <Box ml={1.75} mt={-0.5} mb={2}>
                      <FormHelperText error={true}>
                        {errors.answers}
                      </FormHelperText>
                    </Box>
                  )}
                  <Box my={2} ml={-1.5}>
                    <Button
                      color="primary"
                      startIcon={<Add />}
                      onClick={() =>
                        push(getNewOrderingAnswer(values.answers.length))
                      }
                      data-qa="add-ordering-item-btn"
                    >
                      {t("editQuestion.addItemBtn")}
                    </Button>
                  </Box>
                </Box>
              )}
            />
          </Box>
        )}
      />
      {/* Scoring behavior not necessary for MVP */}
      {/*<Box
        display="flex"
        justifyContent="flex-start"
        mr="auto"
        mb={2}
        mt={4}
        width={{ xs: "auto", sm: 460 }}
      >
        <RadioGroup
          radioButtons={[
            {
              label: t("editQuestion.allOrNothingLabel"),
              value: quizQuestionsScoringBehavior.allOrNothing
            },
            {
              label: t("editQuestion.partialCreditLabel"),
              value: quizQuestionsScoringBehavior.partialCredit
            }
          ]}
          name="scoringBehavior"
          label={t("editQuestion.gradingLabel")}
        />
        </Box>*/}
    </Box>
  );
};

OrderingFields.propTypes = {
  isSurveyQuestion: PropTypes.bool,
  questionFilesList: PropTypes.array,
  setQuestionFilesList: PropTypes.func
};

export default OrderingFields;
